import { HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UpsertToast, WaitForRecordsUploadComplete } from '@mediahaven/shared';
import { selectAll } from '@state/selectors/uploadItem.selector';
import {
    ApiFragment,
    ApiRecordType,
    CustomErrorType,
    FacialRecognitionConsent,
    FailedUploadStatuses,
    FragmentId,
    GenerateGuid,
    isNotNullOrUndefined,
    IUploadItem,
    MediaObjectId,
    NotificationType,
    RecordId,
    RecordStatus,
    Rejection,
    ShConnectionService,
    Toast,
    UploadService,
    UploadStatus,
    UploadType,
    UploadUpdate,
    ZoneId
} from '@stylehaven/components';
import { combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    takeWhile,
    tap
} from 'rxjs/operators';
import { FILE_GROUP, REPRESENTATION_GROUP } from '../../constants/upload-item-group.constants';
import { SharedUploadSandboxService } from './shared-upload.sandbox.service';

@Injectable({ providedIn: 'root' })
export class ResumableUploadSandboxService extends SharedUploadSandboxService {
    private readonly uploadService: UploadService = inject(UploadService);
    private readonly connectionService: ShConnectionService = inject(ShConnectionService);

    private readonly pauseUploadItem$: Subject<string> = new Subject<string>();

    private runningUploadGroup: string | undefined = undefined;

    private readonly MINIMUM_RESUMABLE_UPLOAD_SIZE_MB: number = 100;
    private readonly CONCURRENT_UPLOADS: number = 5;

    // Ensure check after upload finished
    protected readonly AFTER_DIRECT_UPLOAD_FINISHED_STATUS: UploadStatus = UploadStatus.CHECKING_COMPLETION;

    // Queue that creates records
    private readonly recordQueue = new Subject<Observable<IUploadItem>>();

    // Queue that uploads files to records
    private readonly uploadQueue = new Subject<Observable<IUploadItem>>();

    // Queue that uploads files to records
    private readonly directUploadQueue = new Subject<Observable<IUploadItem>>();

    // Queue that checks for record status
    private readonly completionCheckQueue = new Subject<Observable<IUploadItem>>();

    /**
     * How does this work?
     *
     * When a file is added:
     * 1. An UploadItem is created and pushed to the store
     * 2. An Observable to create the record is pushed to the recordQueue
     * 3. Once the record is created, an Observable to upload the file in chuncks is pushed to the uploadQueue
     *
     * On resume:
     * 1 For UploadItems that do not have a record, we push an Observable to create the record to the recordQueue
     * 2 For UploadItems that have a record
     * 2.1 If the UploadItem has a sessionId, we push an Observable to resume the upload to the uploadQueue
     * 2.2 If the UploadItem does not have a sessionId, we push an Observable to start a new upload of the file to the uploadQueue
     */
    constructor() {
        super();
    }

    public init(): void {
        this.initProgressListener();

        this.initQueueListeners();
        this.initResumeListener();
    }

    // #region Listeners
    private initQueueListeners(): void {
        this.recordQueue.pipe(mergeMap(recordObs => recordObs, this.CONCURRENT_UPLOADS)).subscribe(uploadItem => {
            this.queueUpload(uploadItem);
        });

        this.uploadQueue.pipe(mergeMap(uploadObs => uploadObs, this.CONCURRENT_UPLOADS)).subscribe(uploadItem => {
            this.queueUploadCompletionCheck(uploadItem);
        });

        // Does not need concurrency value since the UploadRequestQueue already handles queueing
        this.directUploadQueue.pipe(mergeMap(uploadObs => uploadObs)).subscribe(uploadItem => {
            // Only wait for indexing of new Original Representations
            if (uploadItem.group === REPRESENTATION_GROUP) {
                this.queueUploadCompletionCheck(uploadItem, 5);
            } else {
                const updatedUploadItem: IUploadItem = {
                    ...uploadItem,
                    state: {
                        progress: 100,
                        status: UploadStatus.FINISHED
                    }
                };
                this.syncUploadItem(uploadItem.tempId, updatedUploadItem);
            }
        });

        this.completionCheckQueue
            .pipe(mergeMap(completionCheckObs => completionCheckObs, this.CONCURRENT_UPLOADS))
            .subscribe(uploadItem => {});
    }

    private initResumeListener(): void {
        // Uploads with status Network error will be retried when the
        // network status switches from offline to online
        this.connectionService
            .isOnline()
            .pipe(
                distinctUntilChanged(),
                filter(isOnline => isOnline),
                switchMap(() => this.store.select(selectAll).pipe(take(1))), // Fetch latest uploads, but we don't want to react to new updates
                map(uploads => uploads.filter(upload => upload.state.status === UploadStatus.NETWORK_ERROR))
            )
            .subscribe(uploads => {
                uploads.forEach(upload => {
                    this.resumeUpload(upload);
                });
            });
    }
    // #endregion

    // #region Queue Upload
    public uploadFile(
        file: File,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group?: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): void {
        this.onUpload(file, config);
    }

    public uploadFiles(
        files: FileList,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group?: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<boolean> {
        const filesArray = Array.from(files);

        filesArray.forEach(file => this.onUpload(file, config));

        const fileNames = filesArray.map(file => file.name);

        return this.getAllUploadItems().pipe(
            map(uploadItems => uploadItems.filter(storedUploadItem => fileNames.includes(storedUploadItem.name))),
            filter(uploadItems => uploadItems.every(uploadItem => !this.isRunningUpload(uploadItem))),
            tap(() => {
                if (!this.runningUploadGroup) {
                    console.warn('Running upload group was not set when expecting it to be set');
                    this.finishGroupToast(FILE_GROUP);
                } else {
                    this.finishGroupToast(this.runningUploadGroup);
                    this.runningUploadGroup = undefined;
                }
            }),
            map(() => true)
        );
    }

    public override uploadNewOriginal(file: File, fragmentId: FragmentId): Observable<boolean> {
        const uploadItem = this.onNewOriginalUpload(file, fragmentId);

        return this.getAllUploadItems().pipe(
            map(uploadItems => uploadItems.find(storedUploadItem => storedUploadItem.tempId === uploadItem.tempId)),
            // Don't yield control of stream back to ActionSandbox while upload is still running
            filter((uploadItem): uploadItem is IUploadItem => !!uploadItem && !this.isRunningUpload(uploadItem)),
            map(uploadItem => {
                if (uploadItem.state.status === UploadStatus.INDEX_TIMEOUT) {
                    throw CustomErrorType.IndexTimeout;
                }
                return uploadItem?.state.status === UploadStatus.FINISHED;
            })
        );
    }

    private onUpload(
        file: File,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group?: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): void {
        if (!this.runningUploadGroup) {
            this.runningUploadGroup = `${FILE_GROUP}-${GenerateGuid()}`;
        }

        const uploadItem = this.createUploadItem(file, this.runningUploadGroup);

        this.startGroupToast(uploadItem.group);

        // Add record-creation observable to recordQueue
        // After record created, upload will be queued
        this.queueRecordCreation(
            uploadItem,
            config.zoneId,
            config.parentRecordId,
            config.recordType,
            config.facialRecognitionConsent
        );
    }

    public onNewOriginalUpload(file: File, fragmentId: FragmentId): IUploadItem {
        const uploadItem = this.createUploadItem(file, REPRESENTATION_GROUP, fragmentId);

        this.startGroupToast(uploadItem.group);

        // Add record-creation observable to recordQueue
        // After record created, upload will be queued
        this.queueRepresentationCreation(uploadItem, fragmentId);

        return uploadItem;
    }

    private createUploadItem(file: File, group: string, parent?: FragmentId): IUploadItem {
        // Create new
        const uploadItem: IUploadItem = this.generateUploadItem(file, parent, group);

        // Dispatch UploadItem
        this.initUploadItem(uploadItem);

        return uploadItem;
    }

    private queueRecordCreation(
        uploadItem: IUploadItem,
        zoneId: string,
        parentRecordId: MediaObjectId,
        recordType: ApiRecordType,
        facialRecognitionConsent?: FacialRecognitionConsent
    ): void {
        if (this.isSmallUpload(uploadItem.session?.file)) {
            this.queueDirectRecordUpload(uploadItem, zoneId, parentRecordId, recordType, facialRecognitionConsent);
            return;
        }

        const recordCreation$ = this.recordsService.createResumableUploadRecord(
            uploadItem.session?.file as File,
            zoneId as ZoneId,
            recordType,
            parentRecordId,
            facialRecognitionConsent
        );

        this.queueCreation(recordCreation$, uploadItem);
    }

    private queueRepresentationCreation(uploadItem: IUploadItem, fragmentId: FragmentId): void {
        if (this.isSmallUpload(uploadItem.session?.file)) {
            this.queueDirectRepresentationUpload(uploadItem, fragmentId);
            return;
        }

        const representationCreation$ = this.recordsService.createResumableUploadRepresentation(
            uploadItem.session?.file as File,
            fragmentId
        );

        this.queueCreation(representationCreation$, uploadItem);
    }

    private isSmallUpload(file?: File): boolean {
        const fileSize = file?.size;
        if (!fileSize) {
            return false;
        }
        const fileSizeMB = fileSize / (1024 * 1024);
        return fileSizeMB <= this.MINIMUM_RESUMABLE_UPLOAD_SIZE_MB;
    }
    // #endregion

    // #region Queue resumable upload
    private queueCreation(record$: Observable<ApiFragment>, uploadItem: IUploadItem): void {
        const recordCreation$ = record$.pipe(
            switchMap(record => this.checkIndexed([record])),
            shareReplay(1),
            map(([record]) => {
                // Update uploadItem with recordId
                const updatedUploadItem: IUploadItem = {
                    ...uploadItem,
                    id: record.getRecordId()
                };

                this.syncUploadItem(uploadItem.tempId, updatedUploadItem);

                return updatedUploadItem;
            }),
            catchError(error => {
                console.error('Error creating record for upload resumable', error);
                const updatedUploadItem: IUploadItem = {
                    ...uploadItem,
                    state: {
                        progress: 100,
                        status: UploadStatus.FAILED
                    }
                };

                this.syncUploadItem(uploadItem.tempId, updatedUploadItem);

                return of(updatedUploadItem);
            })
        );

        this.recordQueue.next(recordCreation$);
    }

    private queueUpload(uploadItem: IUploadItem): void {
        const cancel$: Observable<string | null> = this.initCancelUploadItemStream(uploadItem).pipe(startWith(null));
        const pause$: Observable<string | null> = this.initPauseUploadItemStream(uploadItem).pipe(startWith(null));

        const cancelOrPause$: Observable<(string | null)[]> = combineLatest([cancel$, pause$]).pipe(
            filter(([cancel, pause]) => cancel !== null || pause !== null)
        );

        const uploadHandling$: Observable<IUploadItem> = this.uploadService
            .uploadFile(uploadItem.session?.file as File, uploadItem.id as RecordId)
            .pipe(
                takeUntil(cancelOrPause$),
                map((uploadUpdate: UploadUpdate) => this.handleUploadUpdate(uploadItem, uploadUpdate)),
                filter((uploadItem: IUploadItem) => uploadItem.state.progress === 100), // Only emit when progress 100% and not failed
                takeWhile(uploadItem => !FailedUploadStatuses.includes(uploadItem.state.status)) // Complete early if failed
            );

        this.uploadQueue.next(uploadHandling$);
    }

    private queueResumedUpload(uploadItem: IUploadItem, sessionId: string): void {
        const cancel$: Observable<string | null> = this.initCancelUploadItemStream(uploadItem).pipe(startWith(null));
        const pause$: Observable<string | null> = this.initPauseUploadItemStream(uploadItem).pipe(startWith(null));

        const cancelOrPause$: Observable<(string | null)[]> = combineLatest([cancel$, pause$]).pipe(
            filter(([cancel, pause]) => cancel !== null || pause !== null)
        );

        const resumedUploadItem: IUploadItem = this.handleUploadResume(uploadItem);

        const uploadHandling$: Observable<IUploadItem> = this.uploadService
            .resumeUpload(uploadItem.session?.file as File, sessionId)
            .pipe(
                takeUntil(cancelOrPause$),
                map((uploadUpdate: UploadUpdate) => this.handleUploadUpdate(resumedUploadItem, uploadUpdate)),
                filter((uploadItem: IUploadItem) => uploadItem.state.progress === 100), // Only emit when progress 100%
                takeWhile(uploadItem => !FailedUploadStatuses.includes(uploadItem.state.status)) // Complete early if failed
            );

        this.uploadQueue.next(uploadHandling$);
    }

    private queueUploadCompletionCheck(uploadItem: IUploadItem, retries?: number): void {
        const completionCheck$ = of(uploadItem).pipe(
            tap(uploadItem => this.handleCheckingUploadCompletion(uploadItem)),
            switchMap(() =>
                WaitForRecordsUploadComplete(uploadItem.id as RecordId, this.recordsService, true, retries)
            ),
            map(record => this.handleUploadFinished(uploadItem, record)),
            catchError(error => {
                if (error === CustomErrorType.IndexTimeout) {
                    // Engine needs to download the file from s3 storage optionally do an md5 check before the record status is changed.
                    // For large files (gigabytes, terrabytes) this can take a long time, but this should not mark the file as failed
                    // Here we re-queue the file, so that large files don't block smaller files which might complete more quickly
                    const retryCount: number = Math.floor((retries ?? 6) / 2);
                    if (retryCount > 0) {
                        this.queueUploadCompletionCheck(uploadItem, retryCount);
                        return EMPTY;
                    }
                    return of(this.handleUploadTimeout(uploadItem));
                }
                return of(this.handleUploadError(uploadItem));
            })
        );

        this.completionCheckQueue.next(completionCheck$);
    }
    // #endregion

    // #region Queue direct upload
    private queueDirectRecordUpload(
        uploadItem: IUploadItem,
        zoneId: string,
        parentRecordId: MediaObjectId,
        recordType: ApiRecordType,
        facialRecognitionConsent?: FacialRecognitionConsent
    ): void {
        const cancel$: Observable<string> = this.initCancelUploadItemStream(uploadItem).pipe(
            startWith(null),
            isNotNullOrUndefined()
        );

        const uploadHandling$: Observable<HttpEvent<unknown>> = this.directRecordUpload(
            uploadItem.session?.file as File,
            uploadItem,
            cancel$,
            {
                zoneId,
                recordType,
                parentRecordId,
                facialRecognitionConsent
            }
        );

        this.queueDirectUpload(uploadHandling$, uploadItem);
    }

    private queueDirectRepresentationUpload(uploadItem: IUploadItem, fragmentId: FragmentId): void {
        const cancel$: Observable<string> = this.initCancelUploadItemStream(uploadItem).pipe(
            startWith(null),
            isNotNullOrUndefined()
        );

        const uploadHandling$: Observable<HttpEvent<unknown>> = this.directRepresentationUpload(
            uploadItem.session?.file as File,
            uploadItem,
            cancel$,
            fragmentId
        );

        this.queueDirectUpload(uploadHandling$, uploadItem);
    }

    private queueDirectUpload(upload$: Observable<HttpEvent<unknown>>, uploadItem: IUploadItem): void {
        const afterUploadHandling$: Observable<IUploadItem> = this.uploadToApiFragment(upload$).pipe(
            map(record => ({
                ...uploadItem,
                id: record.getFragmentId()
            }))
        );

        this.directUploadQueue.next(afterUploadHandling$);
    }

    // #region Refresh
    public onBrowserRefresh(): Observable<void> {
        // Abort all uploads
        return this.store.select(selectAll).pipe(
            tap(uploads =>
                uploads
                    .filter(upload => this.isRunningUpload(upload))
                    .forEach(upload => this.cancelOrAbortUpload(upload, true))
            ),
            tap(uploads =>
                uploads
                    .filter(upload => upload.state.status === UploadStatus.NETWORK_ERROR)
                    .forEach(upload => this.failUpload(upload))
            ),
            map(() => void 0)
        );
    }
    // #endregion

    // #region Cancel
    public failUpload(uploadItem: IUploadItem): void {
        if (!uploadItem) {
            return;
        }

        const tempId: string = uploadItem.tempId;
        const uploadItemUpdate: Partial<IUploadItem> = {
            state: {
                status: UploadStatus.FAILED,
                progress: 100
            }
        };

        this.syncUploadItem(tempId, uploadItemUpdate);
    }

    public cancelUpload(syncData: { tempId: string; uploadItemUpdate: Partial<IUploadItem> }): void {
        this.cancelUploadItem$.next(syncData.tempId);
    }
    // #endregion

    // #region Pause
    private initPauseUploadItemStream(uploadItem: IUploadItem): Observable<string> {
        return this.pauseUploadItem$.pipe(
            filter(uploadItemId => uploadItemId === uploadItem.tempId),
            tap(uploadItemId => {
                const uploadItemUpdate: Partial<IUploadItem> = {
                    state: {
                        status: UploadStatus.PAUSED,
                        progress: uploadItem.state.progress
                    }
                };
                this.syncUploadItem(uploadItemId, uploadItemUpdate);
            })
        );
    }

    public pauseUpload(uploadItem: IUploadItem): void {
        this.pauseUploadItem$.next(uploadItem.tempId);
    }
    // #endregion

    // #region Resume
    public resumeUpload(uploadItem: IUploadItem): void {
        const sessionId: string | undefined = uploadItem.session?.sessionId;

        if (!uploadItem.session?.file.size) {
            // We lost our file handle, fail the upload
            this.failUpload(uploadItem);
            return;
        }

        if (!sessionId) {
            this.queueUpload(uploadItem);
        } else {
            this.queueResumedUpload(uploadItem, sessionId);
        }
    }
    // #endregion

    // #region UploadItem
    private generateUploadItem(file: File, parent?: FragmentId, group?: string): IUploadItem {
        const now: Date = new Date();

        return {
            tempId: GenerateGuid(),
            name: file.name,
            type: this.isSmallUpload(file) ? UploadType.DIRECT : UploadType.RESUMABLE,
            state: {
                status: UploadStatus.WAITING,
                progress: 0
            },
            session: {
                file: file
            },
            ...(parent ? { parent: { fragmentId: parent } } : {}),
            date: now,
            group: group ?? '0',
            lastProgressUpdate: now
        };
    }

    private handleUploadUpdate(upload: IUploadItem, uploadUpdate: UploadUpdate): IUploadItem {
        if ([UploadStatus.FAILED, UploadStatus.NETWORK_ERROR].includes(uploadUpdate.status)) {
            return this.handleUploadError(upload, undefined, uploadUpdate);
        }

        const session = {
            file: upload.session?.file as File,
            sessionId: uploadUpdate.sessionId
        };

        const updatedUploadItem: IUploadItem = {
            ...upload,
            session: session,
            state: {
                progress: uploadUpdate.progress,
                status: uploadUpdate.status
            }
        };

        this.syncUploadItem(upload.tempId, updatedUploadItem);

        return updatedUploadItem;
    }

    private handleUploadResume(upload: IUploadItem): IUploadItem {
        const updatedUploadItem: IUploadItem = {
            ...upload,
            state: {
                ...upload.state,
                status: UploadStatus.WAITING
            }
        };

        this.syncUploadItem(upload.tempId, updatedUploadItem);

        return updatedUploadItem;
    }

    private handleCheckingUploadCompletion(upload: IUploadItem): IUploadItem {
        const updatedUploadItem: IUploadItem = {
            ...upload,
            state: {
                ...upload.state,
                status: UploadStatus.CHECKING_COMPLETION
            }
        };

        this.syncUploadItem(upload.tempId, updatedUploadItem);

        return updatedUploadItem;
    }

    private handleUploadFinished(upload: IUploadItem, record: ApiFragment): IUploadItem {
        if (record.getRecordStatus() === RecordStatus.Rejected) {
            const updatedUploadItem: IUploadItem = {
                ...upload,
                state: {
                    ...upload.state,
                    status: UploadStatus.REJECTED
                }
            };

            return this.handleUploadError(updatedUploadItem, this.recordRejectionsToErrorMsg(record));
        }

        const updatedUploadItem: IUploadItem = {
            ...upload,
            state: {
                ...upload.state,
                status: UploadStatus.FINISHED
            }
        };

        this.syncUploadItem(upload.tempId, updatedUploadItem);
        return updatedUploadItem;
    }

    private recordRejectionsToErrorMsg(record: ApiFragment): string {
        let message: string = '';

        const rejectionErrors: Rejection[] = record.getRejections();

        // Just show the first rejection
        for (const rejection of rejectionErrors) {
            if (rejection.Reason === 'MD5_CHECK') {
                const errorMessage: string = this.translateService.instant('uploads.list.errors.upload-duplicate');
                const motivation: string = rejection.Motivation ?? '';
                const recordIdsArray: string = motivation.split(':')[1].trim();
                const recordIds: string[] = recordIdsArray.substring(1, recordIdsArray.length - 1).split(',');
                const duplicateUrls: string[] = recordIds.map(id => `records/${id}`);
                const duplicateLinks: string = duplicateUrls
                    .map(
                        url =>
                            `<a class="sh-link" href="${url}">${this.translateService.instant(
                                'uploads.list.show-duplicate'
                            )}</a>`
                    )
                    .join('<br />');

                message = `${errorMessage}<br />${duplicateLinks}`;
                break;
            }
        }

        return message;
    }

    private handleUploadError(upload: IUploadItem, errorMessage?: string, uploadUpdate?: UploadUpdate): IUploadItem {
        let errorKey: string = 'uploads.list.errors.internal';
        let toastKey: string = 'toasts.upload-failed';

        if (uploadUpdate?.status === UploadStatus.NETWORK_ERROR) {
            errorKey = 'uploads.list.errors.offline';
            toastKey = 'toasts.offline';
            this.connectionService.notifyOffline();
        }

        const message: string = errorMessage ?? this.translateService.instant(errorKey);

        const session = {
            file: upload.session?.file as File,
            sessionId: upload.session?.sessionId ?? uploadUpdate?.sessionId
        };

        const uploadItemUpdate: IUploadItem = {
            ...upload,
            state: {
                // MED-6465: If already specific error state, keep status, else set update status or default to Failed
                status: FailedUploadStatuses.includes(upload.state.status)
                    ? upload.state.status
                    : (uploadUpdate?.status ?? UploadStatus.FAILED),
                progress: 100,
                message
            },
            session
        };

        this.syncUploadItem(upload.tempId, uploadItemUpdate);

        if (uploadItemUpdate.group !== REPRESENTATION_GROUP) {
            const toast: Toast = this.toastFactory.create({
                message: this.translateService.instant(toastKey),
                navigationLink: {
                    route: '/concepts/uploads'
                },
                linkText: this.translateService.instant('toasts.see.details'),
                type: NotificationType.Error
            });
            this.store.dispatch(new UpsertToast(toast));
        }

        return uploadItemUpdate;
    }

    public isPausable(upload: IUploadItem): boolean {
        const pausableStatusses: UploadStatus[] = [UploadStatus.UPLOADING];
        return upload.type === UploadType.RESUMABLE && pausableStatusses.includes(upload.state.status);
    }

    public isResumable(upload: IUploadItem): boolean {
        return [UploadStatus.PAUSED].includes(upload.state.status) && !!upload.session?.sessionId;
    }
    // #endregion
}
